import React, { useEffect, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import EnrollmentService from "../../_services/EnrollmentService";
import { useEnrollmentSelection } from "./components/useEnrollmentSelection";
import SelectableEnrollmentTable, {
  headerCellStyle,
  bodyCellStyle,
} from "./components/SelectableEnrollmentTable";

interface BulkChangePlacementStatusModalProps {
  show: boolean;
  onHide: () => void;
  selectedEnquiryIds: number[];
  enquiryEnrollmentsMap: Record<number, any[]>;
  studentList: any[];
  onSuccess: () => void;
}

interface ResultRow {
  enrollmentId: number;
  status: "changed" | "skipped" | "not_found" | "error";
  previousStatusId?: number | null;
  newStatusId?: number;
  reason?: string;
  error?: string;
}

const statusBadgeStyle = (kind: ResultRow["status"]): React.CSSProperties => {
  const pal: Record<ResultRow["status"], { bg: string; fg: string; border: string }> = {
    changed:   { bg: "#ecfdf5", fg: "#065f46", border: "var(--status-success-border)" },
    skipped:   { bg: "#eff6ff", fg: "#1e3a8a", border: "#bfdbfe" },
    not_found: { bg: "#fefce8", fg: "#854d0e", border: "var(--status-warning-border)" },
    error:     { bg: "#fef2f2", fg: "#991b1b", border: "var(--status-danger-border)" },
  };
  const p = pal[kind];
  return {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 10,
    background: p.bg,
    color: p.fg,
    border: `1px solid ${p.border}`,
    fontSize: "0.7rem",
    fontWeight: 600,
  };
};

const BulkChangePlacementStatusModal: React.FC<BulkChangePlacementStatusModalProps> = ({
  show,
  onHide,
  selectedEnquiryIds,
  enquiryEnrollmentsMap,
  studentList,
  onSuccess,
}) => {
  const [placementStatuses, setPlacementStatuses] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingLookups, setLoadingLookups] = useState(false);
  const [placementStatusId, setPlacementStatusId] = useState<number | "">("");

  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ResultRow[] | null>(null);
  const [summary, setSummary] = useState<any>(null);
  const [error, setError] = useState("");

  const {
    enrollments,
    selectedIds,
    selectedEnrollmentIds,
    toggle,
    toggleAll,
    allSelected,
  } = useEnrollmentSelection(
    selectedEnquiryIds,
    enquiryEnrollmentsMap,
    studentList,
    show,
  );

  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoadingLookups(true);
    setError("");
    EnrollmentService.getLookups()
      .then((resp) => {
        if (cancelled) return;
        setPlacementStatuses(resp.data?.data?.placementStatuses || []);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || "Failed to load options");
      })
      .finally(() => {
        if (!cancelled) setLoadingLookups(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show]);

  const handleClose = () => {
    setPlacementStatusId("");
    setResults(null);
    setSummary(null);
    setError("");
    onHide();
  };

  const handleSubmit = async () => {
    setError("");
    if (!placementStatusId) {
      setError("Pick a placement status.");
      return;
    }
    if (selectedEnrollmentIds.length === 0) {
      setError("Select at least one enrollment.");
      return;
    }
    setSubmitting(true);
    try {
      const resp = await EnrollmentService.bulkChangePlacementStatus({
        enrollmentIds: selectedEnrollmentIds,
        placementStatusId: Number(placementStatusId),
      });
      const data = resp.data?.data || {};
      setResults(data.results || []);
      setSummary(data.summary || null);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Bulk placement status change failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDoneAfterResults = () => {
    handleClose();
    onSuccess();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16 }}>
          {results ? "Change Placement Status — Results" : "Change Placement Status (Bulk)"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && (
          <div
            style={{
              padding: "8px 12px",
              background: "var(--status-danger-bg)",
              color: "var(--status-danger)",
              borderRadius: 6,
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        {!results && (
          <>
            {loadingLookups ? (
              <div style={{ textAlign: "center", padding: 20, color: "var(--text-muted)" }}>
                <i className="fa fa-spinner fa-spin" /> Loading options…
              </div>
            ) : (
              <>
                <Form.Group className="mb-3" style={{ maxWidth: 360 }}>
                  <Form.Label style={{ fontSize: 13, fontWeight: 600 }}>
                    Placement status <span style={{ color: "var(--status-danger)" }}>*</span>
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={placementStatusId}
                    onChange={(e) =>
                      setPlacementStatusId(e.target.value ? Number(e.target.value) : "")
                    }
                  >
                    <option value="">Select placement status…</option>
                    {placementStatuses.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <SelectableEnrollmentTable
                  enrollments={enrollments}
                  selectedIds={selectedIds}
                  allSelected={allSelected}
                  onToggle={toggle}
                  onToggleAll={toggleAll}
                  disabled={submitting}
                  caption={`Enrollments (${selectedEnrollmentIds.length} of ${enrollments.length} selected) — the status is applied to all`}
                />
              </>
            )}
          </>
        )}

        {results && (
          <>
            <div
              style={{
                display: "flex",
                gap: 12,
                marginBottom: 12,
                fontSize: 13,
                color: "var(--text-strong)",
              }}
            >
              <span>
                <strong>{summary?.total ?? results.length}</strong> processed
              </span>
              <span style={{ color: "var(--status-success-text)" }}>
                <strong>{summary?.changed ?? 0}</strong> changed
              </span>
              <span style={{ color: "var(--status-info-text)" }}>
                <strong>{summary?.skipped ?? 0}</strong> skipped
              </span>
              <span style={{ color: "var(--status-danger)" }}>
                <strong>{summary?.failed ?? 0}</strong> failed
              </span>
            </div>

            <div
              style={{
                maxHeight: 360,
                overflow: "auto",
                border: "1px solid var(--border-default)",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={headerCellStyle}>Enrollment</th>
                    <th style={headerCellStyle}>Outcome</th>
                    <th style={headerCellStyle}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r) => (
                    <tr key={r.enrollmentId}>
                      <td style={bodyCellStyle}>#{r.enrollmentId}</td>
                      <td style={bodyCellStyle}>
                        <span style={statusBadgeStyle(r.status)}>{r.status}</span>
                      </td>
                      <td style={bodyCellStyle}>
                        {r.status === "changed" && (
                          <span style={{ color: "var(--text-body)" }}>
                            {r.previousStatusId ?? "—"} → {r.newStatusId}
                          </span>
                        )}
                        {r.status === "skipped" && (
                          <span style={{ color: "var(--text-muted)" }}>
                            {r.reason === "already_at_target_status"
                              ? "Already at this status"
                              : r.reason || ""}
                          </span>
                        )}
                        {r.status === "not_found" && (
                          <span style={{ color: "#854d0e" }}>Enrollment not found</span>
                        )}
                        {r.status === "error" && (
                          <span style={{ color: "var(--status-danger)" }}>{r.error}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        {!results ? (
          <>
            <Button variant="secondary" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || loadingLookups || !placementStatusId || selectedEnrollmentIds.length === 0}
            >
              {submitting ? (
                <>
                  <i className="fa fa-spinner fa-spin" /> Applying…
                </>
              ) : (
                <>Apply ({selectedEnrollmentIds.length})</>
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleDoneAfterResults}>
            Done
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BulkChangePlacementStatusModal;
